import { sendCapiEvent } from "@/services/meta-tracking-service"
import * as Sentry from "@sentry/react"
import ReactPixel from "react-facebook-pixel"

import { generateEventId } from "./meta-attribution"

const PIXEL_ID = import.meta.env.VITE_FACEBOOK_PIXEL_ID

let initialized = false

interface PixelApi {
  init: (
    id: string,
    advancedMatching?: Record<string, unknown>,
    options?: Record<string, unknown>
  ) => void
  pageView: () => void
  track: (event: string, data?: Record<string, unknown>) => void
}

/**
 * Resolve the real Pixel API object.
 *
 * react-facebook-pixel ships a UMD bundle whose API lives on a nested
 * `.default` carrying an `__esModule` flag. Bundler interop differs (the
 * vite/rolldown tooling upgrade changed how that default is unwrapped), so we
 * probe the candidate shapes instead of trusting one import shape. Returns
 * null when no candidate exposes `init`, so callers degrade instead of
 * throwing at bootstrap (SPEEKE-APP-E1).
 */
function resolvePixel(): PixelApi | null {
  const candidates: unknown[] = [
    ReactPixel,
    (ReactPixel as { default?: unknown })?.default,
    (ReactPixel as { default?: { default?: unknown } })?.default?.default,
  ]
  for (const candidate of candidates) {
    if (candidate && typeof (candidate as PixelApi).init === "function") {
      return candidate as PixelApi
    }
  }
  return null
}

const pixel = resolvePixel()

export const initFacebookPixel = (userData?: {
  email?: string
  name?: string
  phone?: string
  externalId?: string
}) => {
  if (PIXEL_ID && !initialized) {
    if (!pixel) {
      // Degraded, not crashed: surface to Sentry so a broken interop (e.g. a
      // bundler upgrade changing the module shape) is visible instead of the
      // Pixel silently going dark. (SPEEKE-APP-E1)
      console.warn("react-facebook-pixel: Pixel API not resolved; skipping init.")
      Sentry.captureMessage(
        "Facebook Pixel API not resolved from react-facebook-pixel; tracking disabled",
        "warning"
      )
      return
    }
    pixel.init(PIXEL_ID)
    initialized = true

    // If user is already logged in, set Advanced Matching immediately
    if (userData) {
      setFbAdvancedMatching(userData)
    }

    console.log("Facebook Pixel Initialized", userData ? "(with Advanced Matching)" : "")
  } else if (!PIXEL_ID) {
    console.warn("Facebook Pixel ID (VITE_FACEBOOK_PIXEL_ID) not found in environment variables.")
  }
}

/**
 * Set Advanced Matching data on the Pixel. Call after user logs in or registers.
 * The Pixel hashes the values automatically (SHA-256).
 */
export const setFbAdvancedMatching = (userData: {
  email?: string
  name?: string
  phone?: string
  externalId?: string
}) => {
  if (!initialized || typeof window.fbq !== "function") return

  const matchData: Record<string, any> = {}
  if (userData.email) matchData.em = userData.email.toLowerCase()
  if (userData.phone) matchData.ph = userData.phone.replace(/\D/g, "")
  if (userData.externalId) matchData.external_id = userData.externalId
  if (userData.name) {
    const parts = userData.name.trim().split(/\s+/)
    matchData.fn = parts[0].toLowerCase()
    if (parts.length > 1) matchData.ln = parts[parts.length - 1].toLowerCase()
  }
  matchData.country = "br"

  window.fbq("init", PIXEL_ID, matchData)
}

/**
 * Track a PageView event. Called on every SPA route change via trackPageViewLoader.
 * PageView is Pixel-only (no CAPI needed - Meta uses it for audience building, not conversions).
 */
export const trackFbPageView = () => {
  if (!initialized || !pixel) return
  pixel.pageView()
}

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void
  }
}

/**
 * Track a Meta event via Pixel (client) + CAPI (server) with automatic deduplication.
 * Callers don't need to think about the two channels - this handles both.
 */

export const trackFbEvent = (eventName: string, data?: Record<string, any>) => {
  const eventId = generateEventId()

  // 1. Pixel (client-side)
  if (initialized) {
    if (typeof window.fbq === "function") {
      window.fbq("track", eventName, data || {}, { eventID: eventId })
    } else if (pixel) {
      pixel.track(eventName, data)
    }
  }

  // 2. CAPI (server-side) - fire-and-forget, same eventId for dedup
  sendCapiEvent(eventName, eventId, data)
}

// Standard Event Wrappers
export const trackFbLead = () => trackFbEvent("Lead", { value: 197, currency: "BRL" })

export const trackFbCompleteRegistration = () => trackFbEvent("CompleteRegistration")

export const trackFbInitiateCheckout = (value?: number, currency?: string) =>
  trackFbEvent("InitiateCheckout", { value, currency })

export const trackFbAddPaymentInfo = () => trackFbEvent("AddPaymentInfo")

export const trackFbSubmitApplication = () => trackFbEvent("SubmitApplication")

export const trackFbStartTrial = (value: string, currency: string = "BRL") =>
  trackFbEvent("StartTrial", { currency, value, predicted_ltv: value })
